import React, { useCallback, useEffect } from 'react';
import { Auth } from 'aws-amplify';
import {
  CognitoUser,
  ISignUpResult,
  ICognitoUserAttributeData,
} from 'amazon-cognito-identity-js';
import API from '@aws-amplify/api';
import { useHistory } from 'react-router-dom';
import toast from 'react-hot-toast';

import {
  getUserByEmail,
  getCompanyByUserID,
  updateUserByEmail,
  getCompanyUserByUser,
} from '@sdk/managers/UsersManager';
import { getTierPackage, TierPackage } from '@utils/User';
import { CompanyRole, CreateUserInput, Tier } from '@sdk/API';
import CONFIG from '@sdk/aws-exports';
import useSWR from 'swr';

async function setAuthorization() {
  CONFIG.aws_appsync_authenticationType = 'AMAZON_COGNITO_USER_POOLS';

  API.configure({
    url: CONFIG.aws_appsync_graphqlEndpoint,
    region: CONFIG.aws_appsync_region,
    graphql_headers: async () => {
      const session = await Auth.currentSession();
      return {
        Authorization: session.getIdToken().getJwtToken(),
      };
    },
  });
}

export type ProfileSetupPayload = Omit<
  CreateUserInput,
  'email' | 'username' | 'id'
>;

export interface SignUpParams {
  username: string;
  password: string;
  attributes?: Record<string, any>;
  validationData?: ICognitoUserAttributeData[];
  clientMetadata?: {
    [key: string]: string;
  };
}

export type DbUser = Record<string, any> & {
  permissions?: TierPackage;
};
export interface AuthContextValue {
  user: CognitoUser | any | null;
  dbUser?: DbUser;
  company?: {
    id: string;
    adminID: string;
    companyID: string;
    name: string;
    domain: string;
    role?: CompanyRole;
  };
  companyAdmin?: {
    id: string;
    adminID: string;
    owner: string;
  };
  loading: boolean;
  resendSignupCode: (username: string) => Promise<any>;
  refetch: (email?: string) => Promise<
    | {
        dbUser?: DbUser;
        company?: {
          id: string;
          adminID: string;
          companyID: string;
          name: string;
          role?: CompanyRole;
        };
        companyAdmin?: {
          id: string;
          adminID: string;
          owner: string;
        };
      }
    | any
  >;
  confirmSignup: (username: string, code: string) => Promise<any>;
  signUp: (
    authParams: SignUpParams
  ) => Promise<undefined | Partial<ISignUpResult>>;
  setupProfile: (
    gqlParams: ProfileSetupPayload
  ) => Promise<undefined | Partial<any>>;
  login: (
    usernameOrEmail: string,
    password: string
  ) => Promise<CognitoUser | any>;
  logout: () => Promise<any>;
  requestPasswordReset: (username: string) => Promise<{
    CodeDeliveryDetails: {
      AttributeName: string;
      DeliveryMedium: string;
      Destination: string;
    };
  }>;
  resetPassword: (
    username: string,
    code: string,
    password: string
  ) => Promise<void>;
  changePassword: (password: string, newPassword: string) => Promise<'SUCCESS'>;
}

const USER_FIELDS = [
  'id',
  'email',
  'firstName',
  'username',
  'lastName',
  'gender',
  'phone',
  'tier',
  'type',
  'cryptowallet',
  'postalCode',
  'premiumPolls',
  'usedPremiumPolls',
  { postalInfo: ['division', 'grc', 'postal'] },
];

export const AuthContext = React.createContext<AuthContextValue | undefined>(
  undefined
);

CONFIG.aws_appsync_authenticationType = 'AWS_IAM';

export const AuthProvider: React.FC = ({ children }) => {
  const history = useHistory<any>();
  const [user, setUser] = React.useState<CognitoUser | any | null>(null);
  const [dbUser, setDbUser] = React.useState<any>();
  const [company, setCompany] = React.useState<any>();
  const [companyAdmin, setCompanyAdmin] = React.useState<any>();
  const [credentials, setCredentials] = React.useState<{
    username: string;
    password: string;
  }>();
  const [loading, setLoading] = React.useState<boolean>(true);

  const onFocus = async () => {
    try {
      await Auth.currentAuthenticatedUser({
        bypassCache: true,
      });
    } catch (err: any) {
      if (err === 'The user is not authenticated') {
        setUser(null);
        setDbUser(undefined);
        setCredentials(undefined);
        setCompany(undefined);
        setCompanyAdmin(undefined);
        await unauthLogin();

        CONFIG.aws_appsync_authenticationType = 'AWS_IAM';
        API.configure(CONFIG);
      } else {
        console.log(err);
      }
    }
  };

  useEffect(() => {
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
    };
  });

  const unauthLogin = useCallback(async () => {
    const unauthUser = await Auth.currentCredentials();
    return unauthUser;
  }, []);

  const refetch = useCallback(
    async (email?: string) => {
      try {
        const {
          data: {
            result: { items },
          },
        }: any = await getUserByEmail(
          [
            {
              items: USER_FIELDS,
            },
          ],
          email || user.attributes?.email
        );
      
        const newDbUser = items?.[0];

        if (!newDbUser) {
          throw new Error(
            'Issue retrieving with your account. If this error persists please contact support.'
          );
        } else {
          if (!newDbUser.tier) {
            newDbUser.tier = Tier.FREE;
          }

          if (
            newDbUser.firstName === null ||
            newDbUser.lastName === null ||
            newDbUser.gender === null
          ) {
   
            setDbUser({
              ...newDbUser,
              tier: newDbUser.tier,
              setupProfile: false,
            });
          } else {
            const tierPackage = await getTierPackage('', newDbUser.tier);
        
            setDbUser({
              ...newDbUser,
              tier: newDbUser.tier,
              permissions: tierPackage,
              setupProfile: true,
            });

            const company: any = await getCompanyByUserID(newDbUser.id);
            if (newDbUser.tier === Tier.ENTERPRISE && !company) {
              history.push('/company/setup');
              return;
            }

            if (company) {
              setCompany(company);
              const _compUser: any = await getCompanyUserByUser(
                company.adminID || newDbUser.id
              );
              const companyUser = _compUser.data?.result?.items?.[0];
              if (companyUser) {
                setCompanyAdmin(companyUser);
              } else {
                setCompanyAdmin(undefined);
              }
            } else {
              setCompany(undefined);
              setCompanyAdmin(undefined);
            }

            return {
              user,
              company,
              companyAdmin,
            };
          }
        }
      } catch (err: any) {
        if (err.code === 'UserNotFoundException') {
          err.message = 'Invalid username or password';
        }
        throw err;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user]
  );

  useEffect(() => {
    Auth.configure(CONFIG);
    const init = async () => {
      setLoading(true);

      // attempt to fetch the info of the user that was already logged in
      try {
        setCredentials(undefined);
        const _user = await Auth.currentAuthenticatedUser();
        console.log(_user);
        if (_user.attributes?.email) {
          setUser(_user);
          await setAuthorization();

          await refetch(_user.attributes.email);
        }
      } catch (err: any) {
        if (err !== 'The user is not authenticated') {
          console.error(err);
        }
        setUser(null);
        unauthLogin();

        CONFIG.aws_appsync_authenticationType = 'AWS_IAM';
        API.configure(CONFIG);
      }
      setLoading(false);
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (dbUser && !dbUser.setupProfile) history.replace('/profile/setup');
  }, [dbUser, history]);

  const resendSignupCode = (username: string) => {
    return Auth.resendSignUp(username);
  };

  const confirmSignup = async (username: string, code: string) => {
    const confirmRes = await Auth.confirmSignUp(username, code);
    if (credentials) {
      console.log('trying auto signin');
      await login(credentials.username, credentials.password).catch((err) =>
        console.error('auto signin post confirmation error', err)
      );
      setCredentials(undefined);
    }
    return confirmRes;
  };

  const setupProfile = async (
    payload: ProfileSetupPayload
  ): Promise<undefined | Partial<any>> => {
    console.log(user);

    if (
      !user ||
      user.firstName === null ||
      user.lastName === null ||
      user.gender === null
    ) {
      toast.error('You are not currently logged in.');
      history.push({
        pathname: '/login',
        state: { from: window.location.pathname },
      });
      return;
    }
    // CONFIG.aws_appsync_authenticationType = 'AWS_IAM';
    // API.configure(CONFIG);

    const result: any = await updateUserByEmail(
      USER_FIELDS,
      user.attributes.email,
      {
        id: user.attributes.sub,
        ...payload,
      }
    );

    const tierPackage = await getTierPackage('', dbUser.tier || Tier.FREE);

    setDbUser({
      id: user.attributes.sub,
      ...payload,
      tier: dbUser.tier || Tier.FREE,
      permissions: tierPackage,
      setupProfile: true,
    });
    console.log('setupProfile', { result });

    return result;
  };

  const signUp = async (
    signupParams: SignUpParams
  ): Promise<undefined | Partial<ISignUpResult>> => {
    setDbUser(null);
    setUser(null);

    const result = await Auth.signUp(signupParams);

    setCredentials({
      username: signupParams.username,
      password: signupParams.password,
    });

    console.log('result', { result });

    return result ? result : undefined;
  };

  const login = async (usernameOrEmail: string, password: string) => {
    try {
      setDbUser(null);
      setUser(null);
      setCredentials(undefined);
      setCompany(undefined);
      setCompanyAdmin(undefined);
      setLoading(true);

      const _user: CognitoUser | any = await Auth.signIn(
        usernameOrEmail,
        password
      );

      if (_user.attributes?.email) {
        setUser(_user);
        await setAuthorization();
        await refetch(_user.attributes?.email);
      }

      return;
    } catch (err: any) {
      if (err.code === 'UserNotFoundException') {
        err.message = 'Invalid username or password';
      }
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = useCallback(async () => {
    const data = await Auth.signOut();
    setUser(null);
    setDbUser(undefined);
    setCredentials(undefined);
    setCompany(undefined);
    setCompanyAdmin(undefined);
    await unauthLogin();

    CONFIG.aws_appsync_authenticationType = 'AWS_IAM';
    API.configure(CONFIG);
    return data;
  }, [unauthLogin]);

  const requestPasswordReset = useCallback(async (username: string) => {
    return Auth.forgotPassword(username);
  }, []);

  const resetPassword = useCallback(
    async (username: string, code: string, password: string) => {
      return Auth.forgotPasswordSubmit(username, code, password);
    },
    []
  );

  const changePassword = useCallback(
    async (password: string, newPassword: string) => {
      if (!user) throw new Error('You are not logged in.');
      return Auth.changePassword(user, password, newPassword);
    },
    [user]
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        dbUser,
        company,
        companyAdmin,
        refetch,
        resendSignupCode,
        confirmSignup,
        signUp,
        login,
        logout,
        requestPasswordReset,
        resetPassword,
        changePassword,
        setupProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const authInstance = React.useContext(AuthContext);

  if (authInstance === undefined) {
    throw new Error(
      'The component using the the context must be a descendant of the AuthContext provider'
    );
  }

  return authInstance;
};
